import type {
  ContinuityObjectState,
  InspectorPhysicalStateChange,
  InspectorPhysicalStateChangeType,
  Ledger,
  PhysicalContinuityObligation,
  PhysicalObjectMobilityStatus,
  PhysicalObjectOperationalStatus,
  PhysicalObjectType,
} from '../types';
import {
  deriveObligationsFromObjects,
  mergePhysicalContinuityLedger,
  normalizeContinuityObjectState,
} from './physicalContinuity.service';
import { buildStableObjectId, resolveContinuityObjectIdentity } from './physicalObjectIdentity.service';
import {
  diagnosePhysicalObjectEntry,
  logPhysicalObjectNormalizationAudit,
  normalizeIncomingPhysicalObject,
} from './physicalObjectNormalization.service';

export const PHYSICAL_EXTRACTION_CORRECTION_MAX = 1;

export interface PhysicalCorrectionBudget {
  logicalCorrectionsUsed: number;
  transportAttemptsUsed: number;
  logicalCorrectionMaximum: number;
}

const physicalCorrectionBudget: PhysicalCorrectionBudget = {
  logicalCorrectionsUsed: 0,
  transportAttemptsUsed: 0,
  logicalCorrectionMaximum: PHYSICAL_EXTRACTION_CORRECTION_MAX,
};

export function resetPhysicalCorrectionBudget(): PhysicalCorrectionBudget {
  physicalCorrectionBudget.logicalCorrectionsUsed = 0;
  physicalCorrectionBudget.transportAttemptsUsed = 0;
  physicalCorrectionBudget.logicalCorrectionMaximum = PHYSICAL_EXTRACTION_CORRECTION_MAX;
  return { ...physicalCorrectionBudget };
}

export function getPhysicalCorrectionBudget(): PhysicalCorrectionBudget {
  return { ...physicalCorrectionBudget };
}

export function canAttemptPhysicalCorrection(): boolean {
  return physicalCorrectionBudget.logicalCorrectionsUsed < PHYSICAL_EXTRACTION_CORRECTION_MAX;
}

export function recordPhysicalCorrectionUsed(): void {
  physicalCorrectionBudget.logicalCorrectionsUsed += 1;
  logPhysicalCorrectionBudget();
}

export function recordPhysicalCorrectionTransportAttempt(): void {
  physicalCorrectionBudget.transportAttemptsUsed += 1;
  logPhysicalCorrectionBudget();
}

export function logPhysicalCorrectionBudget(): void {
  console.log(`[PHYSICAL CORRECTION BUDGET] ${JSON.stringify(getPhysicalCorrectionBudget())}`);
}

export interface PhysicalCandidateDiagnostic {
  candidateId: string;
  source: 'DETERMINISTIC' | 'INSPECTOR';
  canonicalName: string;
  objectType: PhysicalObjectType;
  signalType: PhysicalObjectCandidate['signalType'];
  confidence: PhysicalObjectCandidate['confidence'];
  continuityImportance: PhysicalObjectCandidate['continuityImportance'];
  requiresStructuredObject: boolean;
  requiresOpenObligation: boolean;
  matchedObjectId: string | null;
  validationFailure: string | null;
}

export interface PhysicalArchivistResponseShape {
  responseKeys: string[];
  continuityObjectCount: number;
  obligationCount: number;
  objectKeySets: string[][];
  normalizationAcceptedCount: number;
  normalizationRejectedCount: number;
  rejectionCodes: string[];
  schemaMismatchKeys?: string[];
}

export interface PhysicalObjectCandidate {
  candidateId: string;
  source: 'DETERMINISTIC' | 'INSPECTOR';
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  signalType:
    | 'VEHICLE_ARRIVAL'
    | 'ENGINE_FAILURE'
    | 'DISABLED_VEHICLE'
    | 'STRANDED_MACHINERY'
    | 'WEAPON_TRANSFER'
    | 'WEAPON_DESTRUCTION'
    | 'ARTIFACT_POSSESSION'
    | 'CRITICAL_CONTAINER'
    | 'EQUIPMENT_DAMAGE'
    | 'MOBILITY_AID_DAMAGE'
    | 'OBJECT_LEFT_BEHIND';
  objectType: PhysicalObjectType;
  canonicalNameHint: string;
  ownerHint?: string;
  locationHint?: string;
  operationalHint?: PhysicalObjectOperationalStatus;
  mobilityHint?: PhysicalObjectMobilityStatus;
  continuityImportance: 'RELEVANT' | 'REQUIRED_CARRYOVER';
  requiresOpenObligation: boolean;
  sourceChapter: number;
}

export interface PhysicalObjectValidationIssue {
  candidateId?: string;
  objectId?: string;
  field: string;
  code: string;
  message: string;
}

export interface PhysicalContinuityExtractionValidation {
  valid: boolean;
  detectedCandidates: PhysicalObjectCandidate[];
  normalizedObjects: ContinuityObjectState[];
  missingRequiredObjects: PhysicalObjectCandidate[];
  incompleteObjectStates: PhysicalObjectValidationIssue[];
  missingRequiredObligations: PhysicalObjectValidationIssue[];
  repairRequired: boolean;
  blockingReasons: string[];
}

export class PhysicalContinuityExtractionError extends Error {
  readonly code = 'PHYSICAL_CONTINUITY_EXTRACTION_BLOCKED';

  constructor(
    message: string,
    readonly details: {
      chapterNumber: number;
      candidateCount: number;
      missingObjectCount: number;
      incompleteStateCount: number;
      missingObligationCount: number;
      correctionAttempted: boolean;
      correctionSucceeded: boolean;
    },
    readonly archivistRawSnapshot?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'PhysicalContinuityExtractionError';
  }
}

function slugify(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 48);
}

export { buildStableObjectId } from './physicalObjectIdentity.service';

export function normalizeInspectorPhysicalStateChanges(raw: unknown): InspectorPhysicalStateChange[] {
  if (!Array.isArray(raw)) return [];
  const results: InspectorPhysicalStateChange[] = [];
  for (const [index, entry] of raw.entries()) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as Record<string, unknown>;
    const canonicalName = typeof record.canonicalName === 'string' ? record.canonicalName.trim() : '';
    const objectType = String(record.objectType || 'OTHER').toUpperCase() as PhysicalObjectType;
    const changeType = String(record.changeType || 'INTRODUCED').toUpperCase() as InspectorPhysicalStateChange['changeType'];
    const beatId = typeof record.beatId === 'string' ? record.beatId : 'beat-1';
    const confidence = String(record.confidence || 'MEDIUM').toUpperCase() as InspectorPhysicalStateChange['confidence'];
    if (!canonicalName) continue;
    results.push({
      candidateId: typeof record.candidateId === 'string'
        ? record.candidateId
        : `inspector_ch_${index + 1}_${canonicalName.toLowerCase().replace(/\s+/g, '_')}`,
      canonicalName,
      objectType,
      changeType,
      ownerOrHolder: typeof record.ownerOrHolder === 'string' ? record.ownerOrHolder : undefined,
      location: typeof record.location === 'string' ? record.location : undefined,
      unresolvedAtChapterEnd: record.unresolvedAtChapterEnd !== false,
      beatId,
      confidence: confidence === 'HIGH' || confidence === 'LOW' ? confidence : 'MEDIUM',
    });
  }
  return results;
}

function isStaticEnvironmentalRemnant(change: InspectorPhysicalStateChange): boolean {
  const name = change.canonicalName.toLowerCase();
  if (/\b(remnant|wreckage|ruin|debris|hulk|derelict|shell|scrap|abandoned\s+hulk)\b/i.test(name)) {
    return true;
  }
  if (change.changeType === 'INTRODUCED' && !change.ownerOrHolder) {
    return true;
  }
  if (
    (change.changeType === 'DISABLED' || change.changeType === 'DAMAGED') &&
    change.objectType === 'MACHINE' &&
    !change.ownerOrHolder &&
    /\b(loader|excavator|crane|remnant|wreck)\b/i.test(name)
  ) {
    return true;
  }
  return false;
}

function inspectorSignalType(change: InspectorPhysicalStateChange): PhysicalObjectCandidate['signalType'] {
  const disabled = change.changeType === 'DISABLED' || change.changeType === 'DAMAGED';
  const destroyed = change.changeType === 'DESTROYED';
  const carried = change.changeType === 'CARRIED_FORWARD';
  const transferred = change.changeType === 'TRANSFERRED';
  if (disabled && change.objectType === 'VEHICLE') return 'DISABLED_VEHICLE';
  if (disabled || change.changeType === 'ABANDONED') return 'STRANDED_MACHINERY';
  if (destroyed) return 'WEAPON_DESTRUCTION';
  if (transferred && change.objectType === 'WEAPON') return 'WEAPON_TRANSFER';
  if (carried && (change.objectType === 'ARTIFACT' || change.objectType === 'CONTAINER')) {
    return 'CRITICAL_CONTAINER';
  }
  if (carried) return 'ARTIFACT_POSSESSION';
  if (/\b(cell|battery|power|reactor|core)\b/i.test(change.canonicalName)) return 'CRITICAL_CONTAINER';
  return 'VEHICLE_ARRIVAL';
}

function inspectorContinuityImportance(
  change: InspectorPhysicalStateChange,
): PhysicalObjectCandidate['continuityImportance'] {
  if (change.confidence !== 'HIGH' || !change.unresolvedAtChapterEnd) return 'RELEVANT';
  if (isStaticEnvironmentalRemnant(change)) return 'RELEVANT';

  const affectsLaterLogic = (
    changeType: InspectorPhysicalStateChangeType,
    objectType: PhysicalObjectType,
  ): boolean => {
    if (changeType === 'CARRIED_FORWARD') return true;
    if (changeType === 'TRANSFERRED' && objectType === 'WEAPON') return true;
    if (changeType === 'DESTROYED' && objectType === 'WEAPON') return true;
    if ((changeType === 'DISABLED' || changeType === 'DAMAGED') && objectType === 'VEHICLE') return true;
    if (
      (changeType === 'DISABLED' || changeType === 'DAMAGED') &&
      objectType === 'MACHINE' &&
      Boolean(change.ownerOrHolder)
    ) {
      return true;
    }
    if (
      changeType === 'CARRIED_FORWARD' &&
      (objectType === 'CONTAINER' || objectType === 'ARTIFACT') &&
      /\b(cell|battery|power|core)\b/i.test(change.canonicalName)
    ) {
      return true;
    }
    return false;
  };

  return affectsLaterLogic(change.changeType, change.objectType) ? 'REQUIRED_CARRYOVER' : 'RELEVANT';
}

function inspectorRequiresOpenObligation(change: InspectorPhysicalStateChange): boolean {
  if (inspectorContinuityImportance(change) !== 'REQUIRED_CARRYOVER') return false;
  if (isStaticEnvironmentalRemnant(change)) return false;
  if (change.changeType === 'DISABLED' && ['VEHICLE', 'MACHINE'].includes(change.objectType) && change.ownerOrHolder) {
    return true;
  }
  if (change.changeType === 'DESTROYED') return true;
  if (change.changeType === 'ABANDONED') return true;
  return false;
}

export function inspectorChangesToCandidates(
  changes: InspectorPhysicalStateChange[],
  chapterNumber: number,
): PhysicalObjectCandidate[] {
  return changes.map((change) => {
    const disabled = change.changeType === 'DISABLED' || change.changeType === 'DAMAGED';
    const destroyed = change.changeType === 'DESTROYED';
    const carried = change.changeType === 'CARRIED_FORWARD';
    const signalType = inspectorSignalType(change);
    const continuityImportance = inspectorContinuityImportance(change);
    return {
      candidateId: change.candidateId,
      source: 'INSPECTOR' as const,
      confidence: change.confidence,
      signalType,
      objectType: change.objectType,
      canonicalNameHint: change.canonicalName,
      ownerHint: change.ownerOrHolder,
      locationHint: change.location,
      operationalHint: disabled ? 'DISABLED' : destroyed ? 'DESTROYED' : carried ? 'OPERATIONAL' : undefined,
      mobilityHint: disabled ? 'IMMOBILE' : carried ? 'CARRIED' : destroyed ? 'LEFT_BEHIND' : undefined,
      continuityImportance,
      requiresOpenObligation: inspectorRequiresOpenObligation(change),
      sourceChapter: chapterNumber,
    };
  });
}

function candidateDedupKey(candidate: PhysicalObjectCandidate): string {
  return `${candidate.objectType}:${candidate.canonicalNameHint.toLowerCase()}:${candidate.signalType}`;
}

export function inspectorChangeSupportedInFinalProse(
  change: InspectorPhysicalStateChange,
  prose: string,
  chapterNumber: number,
): boolean {
  if (!prose?.trim()) return false;
  const proseLower = prose.toLowerCase();
  const nameLower = change.canonicalName.toLowerCase();
  if (proseLower.includes(nameLower)) return true;

  const deterministic = detectPhysicalObjectCandidates(prose, chapterNumber);
  if (
    deterministic.some(
      (candidate) =>
        candidate.objectType === change.objectType &&
        (candidate.canonicalNameHint.toLowerCase() === nameLower ||
          candidate.canonicalNameHint.toLowerCase().includes(nameLower) ||
          nameLower.includes(candidate.canonicalNameHint.toLowerCase())),
    )
  ) {
    return true;
  }

  if (change.objectType === 'VEHICLE' && change.changeType === 'DISABLED') {
    if (
      /\b(?:died|stalled|immobile|disabled|coughed|stopped|failed)\b/i.test(prose) &&
      /\b(?:truck|vehicle|transport|flatbed|motor|engine|cart|machine)\b/i.test(prose)
    ) {
      return true;
    }
  }

  if (change.objectType === 'WEAPON') {
    if (change.changeType === 'TRANSFERRED' && /\b(?:rifle|pistol|blade|weapon|gun|sword)\b/i.test(prose)) {
      return true;
    }
    if (change.changeType === 'DESTROYED' && /\b(?:broke|snapped|shattered|destroyed|useless)\b/i.test(prose)) {
      return true;
    }
  }

  if (change.objectType === 'ARTIFACT' && change.changeType === 'CARRIED_FORWARD') {
    if (proseLower.includes(nameLower) || /\b(?:carried|tucked|pocket|coat|pack)\b/i.test(prose)) {
      return true;
    }
  }

  return false;
}

/** Inspector runs on pre-Editor Scribe output; only keep changes evidenced in final accepted prose. */
export function filterInspectorPhysicalStateChangesForFinalProse(
  changes: InspectorPhysicalStateChange[],
  prose: string,
  chapterNumber: number,
): InspectorPhysicalStateChange[] {
  return changes.filter((change) =>
    inspectorChangeSupportedInFinalProse(change, prose, chapterNumber),
  );
}

export function unionPhysicalCandidates(params: {
  prose: string;
  chapterNumber: number;
  inspectorChanges?: InspectorPhysicalStateChange[];
}): PhysicalObjectCandidate[] {
  const deterministic = detectPhysicalObjectCandidates(params.prose, params.chapterNumber).map((candidate) => ({
    ...candidate,
    source: 'DETERMINISTIC' as const,
    confidence: candidate.continuityImportance === 'REQUIRED_CARRYOVER' ? 'HIGH' as const : 'MEDIUM' as const,
  }));
  const inspector = inspectorChangesToCandidates(params.inspectorChanges || [], params.chapterNumber);
  const merged = new Map<string, PhysicalObjectCandidate>();
  for (const candidate of [...deterministic, ...inspector]) {
    const key = candidateDedupKey(candidate);
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, candidate);
      continue;
    }
    const rank = (value: PhysicalObjectCandidate) =>
      (value.confidence === 'HIGH' ? 3 : value.confidence === 'MEDIUM' ? 2 : 1) +
      (value.continuityImportance === 'REQUIRED_CARRYOVER' ? 2 : 0);
    merged.set(key, rank(candidate) >= rank(existing) ? candidate : existing);
  }
  return [...merged.values()];
}

export function logPhysicalCandidateSources(payload: Record<string, unknown>): void {
  console.log(`[PHYSICAL CANDIDATE SOURCES] ${JSON.stringify(payload)}`);
}

function extractOwnerNear(text: string, index: number): string | undefined {
  const window = text.slice(Math.max(0, index - 120), index + 40);
  const match = window.match(/\b([A-Z][a-z]+)\b/g);
  if (!match?.length) return undefined;
  const filtered = match.filter((name) => !['The', 'She', 'He', 'They', 'It', 'When', 'After', 'Before', 'Steve'].includes(name));
  return filtered[0] || filtered[filtered.length - 1];
}

function extractLocationHint(text: string): string | undefined {
  const patterns = [
    /\boutside\s+([^.!?]{3,60})/i,
    /\bnear\s+([^.!?]{3,60})/i,
    /\bat\s+([^.!?]{3,60})/i,
    /\b(homestead|perimeter|fence|yard|road|pass)\b/i,
  ];
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      const value = (match[1] || match[0]).trim().slice(0, 80);
      if (/homestead/i.test(value) || /homestead/i.test(match[0])) return "outside Steve's homestead";
      return value;
    }
  }
  return undefined;
}

export function detectPhysicalObjectCandidates(
  prose: string,
  chapterNumber: number,
): PhysicalObjectCandidate[] {
  if (!prose?.trim()) return [];
  const text = prose.replace(/\s+/g, ' ');
  const candidates: PhysicalObjectCandidate[] = [];
  let candidateIndex = 0;

  const addCandidate = (partial: Omit<PhysicalObjectCandidate, 'candidateId' | 'sourceChapter' | 'source' | 'confidence'>) => {
    candidateIndex += 1;
    candidates.push({
      ...partial,
      candidateId: `candidate_ch${chapterNumber}_${candidateIndex}`,
      sourceChapter: chapterNumber,
      source: 'DETERMINISTIC',
      confidence: partial.continuityImportance === 'REQUIRED_CARRYOVER' ? 'HIGH' : 'MEDIUM',
    });
  };

  const vehiclePattern = /\b(flatbed|truck|transport|vehicle|skiff|cart|wagon)\b/gi;
  let vehicleMatch: RegExpExecArray | null;
  while ((vehicleMatch = vehiclePattern.exec(text)) !== null) {
    const name = vehicleMatch[1];
    const vehicleWindow = text.slice(vehicleMatch.index, vehicleMatch.index + 220);
    const supplyCart = vehicleWindow.match(/\bsupply\s+cart\b/i);
    const canonicalNameHint = supplyCart ? 'supply cart' : name;
    const lowerWindow = vehicleWindow.toLowerCase();
    const owner = extractOwnerNear(text, vehicleMatch.index);
    const locationHint = extractLocationHint(text.slice(vehicleMatch.index, vehicleMatch.index + 220));
  const engineFailed =
    /\b(?:engine|motor)\b[^.!?]{0,80}\b(?:failed|died|seized|blew|stall(?:ed|ing)?|coughed)\b/i.test(lowerWindow)
    || /\b(?:coolant|seal)\b[^.!?]{0,80}\b(?:lost|failed|blew)\b/i.test(lowerWindow)
    || /\bcould\s+not\s+wake\b/i.test(lowerWindow);
  const disabled = engineFailed
    || /\b(?:disabled|immobile|stranded|dead|stalled)\b/i.test(lowerWindow)
    || /\brolled\s+to\s+a\s+final\s+stop\b/i.test(lowerWindow)
    || /\bleft\s+the\s+machine\s+where\s+it\s+had\s+stalled\b/i.test(lowerWindow);
    const arrived = /\b(?:arriv(?:ed|ing|es)|pull(?:ed|ing)?\s+up|came\s+in)\b/i.test(lowerWindow);

    if (arrived && !disabled) {
      addCandidate({
        signalType: 'VEHICLE_ARRIVAL',
        objectType: 'VEHICLE',
        canonicalNameHint,
        ownerHint: owner,
        locationHint,
        operationalHint: 'OPERATIONAL',
        mobilityHint: 'MOBILE',
        continuityImportance: 'RELEVANT',
        requiresOpenObligation: false,
      });
    }
    if (engineFailed || disabled) {
      addCandidate({
        signalType: disabled ? 'DISABLED_VEHICLE' : 'ENGINE_FAILURE',
        objectType: 'VEHICLE',
        canonicalNameHint,
        ownerHint: owner,
        locationHint,
        operationalHint: 'DISABLED',
        mobilityHint: 'IMMOBILE',
        continuityImportance: 'REQUIRED_CARRYOVER',
        requiresOpenObligation: true,
      });
    }
  }

  const weaponTransferMatch =
    text.match(/\b(?:hand(?:ed|s)?|gave|passed|transferred)\b[^.!?]{0,80}\b(pistol|rifle|sidearm|weapon|blade|knife)\b/i)
    || text.match(/\bpassed\s+the\s+(rifle|pistol|blade|knife|weapon)\b/i);
  if (weaponTransferMatch) {
    const weaponName = weaponTransferMatch[1] || 'weapon';
    addCandidate({
      signalType: 'WEAPON_TRANSFER',
      objectType: 'WEAPON',
      canonicalNameHint: weaponName,
      continuityImportance: 'RELEVANT',
      requiresOpenObligation: false,
    });
  }

  const holsteredWeaponMatch = text.match(/\b(?:his|her|their|the)\s+(pistol|sidearm|rifle|blade|knife)\b[^.!?]{0,40}\b(?:remained|stayed|holstered|secured)\b/i);
  if (holsteredWeaponMatch) {
    addCandidate({
      signalType: 'WEAPON_TRANSFER',
      objectType: 'WEAPON',
      canonicalNameHint: holsteredWeaponMatch[1],
      continuityImportance: 'RELEVANT',
      requiresOpenObligation: false,
    });
  }

  const weaponDestroyedMatch =
    text.match(/\b(?:destroyed|shattered|broke|busted|snapped)\b[^.!?]{0,80}\b(pistol|rifle|sidearm|weapon|blade|knife)\b/i)
    || text.match(/\b(pistol|rifle|sidearm|weapon|blade|knife)\b[^.!?]{0,80}\b(?:destroyed|shattered|broke|busted|snapped)\b/i)
    || text.match(/\b(blade|knife|rifle|pistol)\s+snapped\b/i);
  if (weaponDestroyedMatch) {
    const weaponName = weaponDestroyedMatch[1] || 'weapon';
    addCandidate({
      signalType: 'WEAPON_DESTRUCTION',
      objectType: 'WEAPON',
      canonicalNameHint: weaponName,
      operationalHint: 'DESTROYED',
      continuityImportance: 'REQUIRED_CARRYOVER',
      requiresOpenObligation: true,
    });
  }

  const artifactCarried =
    /\b(?:carried|holding|gripped|secured|tucked)\b[^.!?]{0,80}\b(?:module|artifact|relay|device|core|sample|data\s+core)\b/i.test(text)
    || /\bdata\s+core\b/i.test(text);
  if (artifactCarried) {
    addCandidate({
      signalType: 'ARTIFACT_POSSESSION',
      objectType: 'ARTIFACT',
      canonicalNameHint: 'artifact',
      mobilityHint: 'CARRIED',
      continuityImportance: 'REQUIRED_CARRYOVER',
      requiresOpenObligation: false,
    });
  }

  const deduped = new Map<string, PhysicalObjectCandidate>();
  for (const candidate of candidates) {
    const key =
      candidate.objectType === 'WEAPON'
        ? `${candidate.objectType}:${slugify(candidate.canonicalNameHint)}:${slugify(candidate.ownerHint || '')}`
        : `${candidate.objectType}:${slugify(candidate.canonicalNameHint)}:${slugify(candidate.ownerHint || '')}:${candidate.signalType}`;
    const existing = deduped.get(key);
    const shouldReplace =
      !existing ||
      (candidate.continuityImportance === 'REQUIRED_CARRYOVER' && existing.continuityImportance !== 'REQUIRED_CARRYOVER') ||
      (candidate.signalType === 'WEAPON_DESTRUCTION' && existing.signalType === 'WEAPON_TRANSFER') ||
      (candidate.requiresOpenObligation && !existing.requiresOpenObligation);
    if (shouldReplace) {
      deduped.set(key, candidate);
    }
  }
  return [...deduped.values()];
}

function objectMatchesCandidate(object: ContinuityObjectState, candidate: PhysicalObjectCandidate): boolean {
  if (object.objectId.includes(slugify(candidate.candidateId))) return true;
  const name = object.canonicalName.toLowerCase();
  const hint = candidate.canonicalNameHint.toLowerCase();
  const aliasMatch = object.aliases.some((alias) => alias.toLowerCase().includes(hint) || hint.includes(alias.toLowerCase()));
  const nameMatch = name.includes(hint) || hint.includes(name) || aliasMatch;
  if (!nameMatch || object.objectType !== candidate.objectType) return false;
  if (candidate.ownerHint && object.ownerCharacterIds.length) {
    return object.ownerCharacterIds.some((owner) => owner.toLowerCase() === candidate.ownerHint!.toLowerCase());
  }
  return true;
}

function findMatchingObject(
  objects: ContinuityObjectState[],
  candidate: PhysicalObjectCandidate,
): ContinuityObjectState | undefined {
  return objects.find((object) => objectMatchesCandidate(object, candidate));
}

function validateObjectCompleteness(
  object: ContinuityObjectState,
  candidate: PhysicalObjectCandidate,
): PhysicalObjectValidationIssue[] {
  const issues: PhysicalObjectValidationIssue[] = [];
  const push = (field: string, code: string, message: string) => {
    issues.push({ candidateId: candidate.candidateId, objectId: object.objectId, field, code, message });
  };

  if (!object.objectId) push('objectId', 'MISSING_OBJECT_ID', 'Missing stable object ID');
  if (!object.canonicalName) push('canonicalName', 'MISSING_NAME', 'Missing canonical name');
  if (!object.objectType) push('objectType', 'MISSING_TYPE', 'Missing object type');
  if (!object.sourceChapterId) push('sourceChapterId', 'MISSING_SOURCE_CHAPTER', 'Missing source chapter');

  if (candidate.objectType === 'VEHICLE' && candidate.operationalHint === 'DISABLED') {
    if (!object.ownerCharacterIds.length && !object.currentHolderCharacterId) {
      push('ownerCharacterIds', 'MISSING_OWNER', 'Disabled vehicle requires owner or holder');
    }
    if (!object.currentLocationDescription && !object.currentLocationId) {
      push('currentLocationDescription', 'MISSING_LOCATION', 'Disabled vehicle requires location');
    }
    if (object.operationalStatus !== 'DISABLED' && object.operationalStatus !== 'DAMAGED') {
      push('operationalStatus', 'INVALID_STATUS', 'Disabled vehicle requires DISABLED or DAMAGED status');
    }
    if (!object.mobilityStatus || !['IMMOBILE', 'LEFT_BEHIND', 'UNKNOWN'].includes(object.mobilityStatus)) {
      push('mobilityStatus', 'MISSING_MOBILITY', 'Disabled vehicle requires IMMOBILE mobility');
    }
    if (object.continuityImportance !== 'REQUIRED_CARRYOVER') {
      push('continuityImportance', 'MISSING_IMPORTANCE', 'Disabled vehicle requires REQUIRED_CARRYOVER');
    }
  }

  if (candidate.objectType === 'ARTIFACT' && candidate.mobilityHint === 'CARRIED') {
    if (!object.currentHolderCharacterId && !object.ownerCharacterIds.length) {
      push('currentHolderCharacterId', 'MISSING_HOLDER', 'Carried artifact requires holder');
    }
    if (!object.currentLocationDescription && !object.currentLocationId && !object.currentHolderCharacterId) {
      push('currentLocationDescription', 'MISSING_LOCATION', 'Carried artifact requires holder or location');
    }
  }

  return issues;
}

function obligationRequiredForObject(object: ContinuityObjectState, candidate: PhysicalObjectCandidate): boolean {
  if (!candidate.requiresOpenObligation) return false;
  if (object.continuityImportance === 'BACKGROUND') return false;
  if (object.operationalStatus === 'DISABLED' || object.operationalStatus === 'DAMAGED') return true;
  if (object.operationalStatus === 'DESTROYED' && candidate.signalType === 'WEAPON_DESTRUCTION') return true;
  return candidate.continuityImportance === 'REQUIRED_CARRYOVER';
}

function isRequiredStructuredCandidate(candidate: PhysicalObjectCandidate): boolean {
  if (candidate.continuityImportance !== 'REQUIRED_CARRYOVER' && !candidate.requiresOpenObligation) {
    return false;
  }
  if (candidate.confidence === 'HIGH') return true;
  if (candidate.source === 'DETERMINISTIC') return true;
  return false;
}

export const ARCHIVIST_INITIAL_LEDGER_REQUIRED_FIELDS = [
  'characters',
  'locations',
  'inventory',
  'plot_points',
  'momentum',
  'arc_stage',
  'continuity_objects',
  'physical_continuity_obligations',
] as const;

export const ARCHIVIST_PHYSICAL_MANIFEST_INSTRUCTION = `PHYSICAL CANDIDATE MANIFEST (independent detections from final accepted chapter):
These candidates were independently detected in the final accepted chapter.
Return structured physical state for every REQUIRED_CARRYOVER candidate in continuity_objects.
Do not invent mandatory state for BACKGROUND candidates.
Always return continuity_objects and physical_continuity_obligations arrays (empty when no structured extraction is required).`;

export interface ArchivistPhysicalCandidateManifestEntry {
  candidateId: string;
  canonicalName: string;
  objectType: string;
  signalType: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  continuityImportance: 'BACKGROUND' | 'RELEVANT' | 'REQUIRED_CARRYOVER';
  unresolvedAtChapterEnd: boolean;
  requiresStructuredObject: boolean;
  requiresOpenObligation: boolean;
  requiredFields: string[];
}

function manifestContinuityImportance(
  candidate: PhysicalObjectCandidate,
): ArchivistPhysicalCandidateManifestEntry['continuityImportance'] {
  if (candidate.continuityImportance === 'REQUIRED_CARRYOVER') return 'REQUIRED_CARRYOVER';
  if (candidate.signalType === 'STRANDED_MACHINERY') return 'BACKGROUND';
  return 'RELEVANT';
}

function requiredFieldsForCandidate(candidate: PhysicalObjectCandidate): string[] {
  const base = ['objectId', 'canonicalName', 'objectType', 'sourceChapterId', 'continuityImportance'];
  if (candidate.objectType === 'VEHICLE' && candidate.operationalHint === 'DISABLED') {
    return [...base, 'ownerCharacterIds', 'currentLocationDescription', 'operationalStatus', 'mobilityStatus'];
  }
  if (candidate.mobilityHint === 'CARRIED') {
    return [...base, 'currentHolderCharacterId', 'operationalStatus', 'mobilityStatus'];
  }
  if (candidate.requiresOpenObligation) {
    return [...base, 'operationalStatus', 'mobilityStatus'];
  }
  return base;
}

export function buildArchivistPhysicalCandidateManifest(
  candidates: PhysicalObjectCandidate[],
  inspectorChanges?: InspectorPhysicalStateChange[],
): ArchivistPhysicalCandidateManifestEntry[] {
  const unresolvedById = new Map(
    (inspectorChanges || []).map((change) => [change.candidateId, change.unresolvedAtChapterEnd]),
  );
  return candidates.map((candidate) => ({
    candidateId: candidate.candidateId,
    canonicalName: candidate.canonicalNameHint,
    objectType: candidate.objectType,
    signalType: candidate.signalType,
    confidence: candidate.confidence,
    continuityImportance: manifestContinuityImportance(candidate),
    unresolvedAtChapterEnd:
      candidate.source === 'INSPECTOR'
        ? unresolvedById.get(candidate.candidateId) ?? false
        : candidate.requiresOpenObligation,
    requiresStructuredObject: isRequiredStructuredCandidate(candidate),
    requiresOpenObligation: candidate.requiresOpenObligation,
    requiredFields: requiredFieldsForCandidate(candidate),
  }));
}

export function prepareChapterPhysicalCandidates(params: {
  prose: string;
  chapterNumber: number;
  inspectorChanges?: InspectorPhysicalStateChange[];
}): {
  candidates: PhysicalObjectCandidate[];
  manifest: ArchivistPhysicalCandidateManifestEntry[];
  inspector: InspectorPhysicalStateChange[];
} {
  const inspector = filterInspectorPhysicalStateChangesForFinalProse(
    params.inspectorChanges || [],
    params.prose,
    params.chapterNumber,
  );
  const candidates = unionPhysicalCandidates({
    prose: params.prose,
    chapterNumber: params.chapterNumber,
    inspectorChanges: inspector,
  });
  return {
    candidates,
    manifest: buildArchivistPhysicalCandidateManifest(candidates, inspector),
    inspector,
  };
}

export function normalizeArchivistPhysicalResponse(rawData: Record<string, unknown>): Record<string, unknown> {
  const continuity_objects = Array.isArray(rawData.continuity_objects)
    ? rawData.continuity_objects
    : Array.isArray(rawData.continuityObjects)
      ? rawData.continuityObjects
      : [];

  const physical_continuity_obligations = Array.isArray(rawData.physical_continuity_obligations)
    ? rawData.physical_continuity_obligations
    : Array.isArray(rawData.physicalContinuityObligations)
      ? rawData.physicalContinuityObligations
      : [];

  return {
    ...rawData,
    continuity_objects,
    physical_continuity_obligations,
  };
}

export function analyzePhysicalArchivistResponseShape(
  rawData: Record<string, unknown>,
  chapterNumber: number,
  context?: { projectId?: string; existingObjects?: ContinuityObjectState[] },
): PhysicalArchivistResponseShape {
  const normalized = normalizeArchivistPhysicalResponse(rawData);
  const responseKeys = Object.keys(normalized);
  const schemaMismatchKeys: string[] = [];
  if (!Array.isArray(rawData.continuity_objects) && Array.isArray(rawData.continuityObjects)) {
    schemaMismatchKeys.push('continuityObjects');
  }
  if (!Array.isArray(rawData.physical_continuity_obligations) && Array.isArray(rawData.physicalContinuityObligations)) {
    schemaMismatchKeys.push('physicalContinuityObligations');
  }

  const incomingObjects = Array.isArray(normalized.continuity_objects)
    ? normalized.continuity_objects
    : [];

  const obligations = Array.isArray(normalized.physical_continuity_obligations)
    ? normalized.physical_continuity_obligations
    : [];

  const objectKeySets: string[][] = [];
  const rejectionCodes: string[] = [];
  let normalizationAcceptedCount = 0;
  let normalizationRejectedCount = 0;

  for (const [index, entry] of incomingObjects.entries()) {
    const audit = diagnosePhysicalObjectEntry(entry, index, 'PARSED_RESPONSE');
    objectKeySets.push(audit.originalKeySet);
    if (audit.rejectionCode === 'EMPTY_PHYSICAL_OBJECT') {
      normalizationRejectedCount += 1;
      rejectionCodes.push('EMPTY_PHYSICAL_OBJECT');
      logPhysicalObjectNormalizationAudit(audit);
      continue;
    }
    if (!entry || typeof entry !== 'object') {
      normalizationRejectedCount += 1;
      rejectionCodes.push('INVALID_ENTRY');
      continue;
    }
    const normalized = normalizeContinuityObjectState(entry, chapterNumber, index, context);
    if (normalized) {
      normalizationAcceptedCount += 1;
    } else {
      normalizationRejectedCount += 1;
      const record = entry as Record<string, unknown>;
      if (audit.rejectionCode === 'UNRECOGNIZED_OBJECT_KEYS') {
        rejectionCodes.push('UNRECOGNIZED_OBJECT_KEYS');
      } else if (audit.rejectionCode === 'MISSING_IDENTITY_FIELDS') {
        rejectionCodes.push('MISSING_IDENTITY_FIELDS');
      } else if (!record.canonicalName && !record.name && !record.objectName) {
        rejectionCodes.push('MISSING_CANONICAL_NAME');
      } else {
        rejectionCodes.push('NORMALIZATION_REJECTED');
      }
      logPhysicalObjectNormalizationAudit({
        ...audit,
        rejectionCode: rejectionCodes[rejectionCodes.length - 1],
      });
    }
  }

  return {
    responseKeys,
    continuityObjectCount: incomingObjects.length,
    obligationCount: obligations.length,
    objectKeySets,
    normalizationAcceptedCount,
    normalizationRejectedCount,
    rejectionCodes: [...new Set(rejectionCodes)],
    ...(schemaMismatchKeys.length ? { schemaMismatchKeys } : {}),
  };
}

export function logPhysicalArchivistResponseShape(
  rawData: Record<string, unknown>,
  chapterNumber: number,
  context?: { projectId?: string; existingObjects?: ContinuityObjectState[] },
): PhysicalArchivistResponseShape {
  const shape = analyzePhysicalArchivistResponseShape(rawData, chapterNumber, context);
  console.log(`[PHYSICAL ARCHIVIST RESPONSE SHAPE] ${JSON.stringify(shape)}`);
  return shape;
}

export function buildPhysicalCandidateDiagnostics(params: {
  candidates: PhysicalObjectCandidate[];
  validation: PhysicalContinuityExtractionValidation;
  mergedPreview: Ledger;
}): PhysicalCandidateDiagnostic[] {
  return params.candidates.map((candidate) => {
    const requiresStructuredObject = isRequiredStructuredCandidate(candidate);
    const object = findMatchingObject(params.mergedPreview.continuity_objects || [], candidate);
    const missing = params.validation.missingRequiredObjects.some((entry) => entry.candidateId === candidate.candidateId);
    const incomplete = params.validation.incompleteObjectStates.find((issue) => issue.candidateId === candidate.candidateId);
    const obligationMissing = params.validation.missingRequiredObligations.find(
      (issue) => issue.candidateId === candidate.candidateId,
    );

    let validationFailure: string | null = null;
    if (missing) validationFailure = 'MISSING_REQUIRED_OBJECT';
    else if (incomplete) validationFailure = incomplete.code;
    else if (obligationMissing) validationFailure = obligationMissing.code;

    return {
      candidateId: candidate.candidateId,
      source: candidate.source,
      canonicalName: candidate.canonicalNameHint,
      objectType: candidate.objectType,
      signalType: candidate.signalType,
      confidence: candidate.confidence,
      continuityImportance: candidate.continuityImportance,
      requiresStructuredObject,
      requiresOpenObligation: candidate.requiresOpenObligation,
      matchedObjectId: object?.objectId || null,
      validationFailure,
    };
  });
}

export function logPhysicalCandidateDiagnostics(diagnostics: PhysicalCandidateDiagnostic[]): void {
  console.log(`[PHYSICAL CANDIDATE DIAGNOSTICS] ${JSON.stringify(diagnostics)}`);
}

export function validatePhysicalContinuityExtraction(params: {
  candidates: PhysicalObjectCandidate[];
  rawData: Record<string, unknown>;
  chapterNumber: number;
  existingLedger: Ledger;
  projectId?: string;
  manifest?: ArchivistPhysicalCandidateManifestEntry[];
}): PhysicalContinuityExtractionValidation {
  const manifest = params.manifest || [];
  const incomingObjects = Array.isArray(params.rawData.continuity_objects) ? params.rawData.continuity_objects : [];
  const normalizedObjects = incomingObjects
    .map((entry, index) =>
      normalizeIncomingPhysicalObject(entry, {
        chapterNumber: params.chapterNumber,
        itemIndex: index,
        manifest,
        projectId: params.projectId,
        existingObjects: params.existingLedger.continuity_objects,
      }).object,
    )
    .filter((entry): entry is ContinuityObjectState => Boolean(entry));

  const mergedPreview = mergePhysicalContinuityLedger(
    params.existingLedger,
    params.rawData,
    params.chapterNumber,
    { projectId: params.projectId },
  );
  const obligations = mergedPreview.physical_continuity_obligations || [];

  const requiredCandidates = params.candidates.filter((candidate) => isRequiredStructuredCandidate(candidate));

  const missingRequiredObjects: PhysicalObjectCandidate[] = [];
  const incompleteObjectStates: PhysicalObjectValidationIssue[] = [];
  const missingRequiredObligations: PhysicalObjectValidationIssue[] = [];

  for (const [index, entry] of incomingObjects.entries()) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as Record<string, unknown>;
    const canonicalName = String(record.canonicalName || record.name || '');
    const objectType = String(record.objectType || 'OTHER').toUpperCase() as PhysicalObjectType;
    const ownerCharacterIds = Array.isArray(record.ownerCharacterIds)
      ? record.ownerCharacterIds.map(String)
      : record.owner
        ? [String(record.owner)]
        : [];
    const resolution = resolveContinuityObjectIdentity({
      projectId: params.projectId,
      chapterNumber: params.chapterNumber,
      index,
      canonicalName,
      objectType,
      aliases: Array.isArray(record.aliases) ? record.aliases.map(String) : [],
      ownerCharacterIds,
      suppliedObjectId: typeof record.objectId === 'string' ? record.objectId : undefined,
      existingObjects: params.existingLedger.continuity_objects,
    });
    if (resolution.ambiguous) {
      incompleteObjectStates.push({
        objectId: resolution.objectId,
        field: 'objectId',
        code: 'ENTITY_RESOLUTION_AMBIGUOUS',
        message: `Ambiguous identity for ${canonicalName}`,
      });
    }
  }

  for (const candidate of requiredCandidates) {
    const object = findMatchingObject(mergedPreview.continuity_objects || [], candidate);
    if (!object) {
      missingRequiredObjects.push(candidate);
      continue;
    }
    incompleteObjectStates.push(...validateObjectCompleteness(object, candidate));
    if (obligationRequiredForObject(object, candidate)) {
      const hasOpen = obligations.some(
        (obligation) => obligation.objectId === object.objectId && obligation.status === 'OPEN',
      );
      if (!hasOpen) {
        missingRequiredObligations.push({
          candidateId: candidate.candidateId,
          objectId: object.objectId,
          field: 'physical_continuity_obligations',
          code: 'MISSING_OPEN_OBLIGATION',
          message: `Open obligation required for ${object.objectId}`,
        });
      }
    }
  }

  const blockingReasons = [
    ...missingRequiredObjects.map((candidate) => `missing_object:${candidate.candidateId}`),
    ...incompleteObjectStates.map((issue) => `${issue.code}:${issue.field}`),
    ...missingRequiredObligations.map((issue) => issue.code),
  ];

  const valid =
    missingRequiredObjects.length === 0 &&
    incompleteObjectStates.length === 0 &&
    missingRequiredObligations.length === 0;

  return {
    valid,
    detectedCandidates: params.candidates,
    normalizedObjects,
    missingRequiredObjects,
    incompleteObjectStates,
    missingRequiredObligations,
    repairRequired: !valid,
    blockingReasons,
  };
}

export function buildPhysicalCorrectionPayload(params: {
  chapterNumber: number;
  validation: PhysicalContinuityExtractionValidation;
  currentPhysicalOutput: Record<string, unknown>;
}): Record<string, unknown> {
  const continuity_objects = Array.isArray(params.currentPhysicalOutput.continuity_objects)
    ? [...(params.currentPhysicalOutput.continuity_objects as unknown[])]
    : [];
  const physical_continuity_obligations = Array.isArray(params.currentPhysicalOutput.physical_continuity_obligations)
    ? [...(params.currentPhysicalOutput.physical_continuity_obligations as unknown[])]
    : [];

  for (const candidate of params.validation.missingRequiredObjects) {
    const objectId = buildStableObjectId({
      projectId: undefined,
      canonicalName: candidate.canonicalNameHint,
      objectType: candidate.objectType,
      firstSeenChapter: params.chapterNumber,
      sourceCreationRecordId: candidate.candidateId,
    });
    continuity_objects.push({
      objectId,
      canonicalName: candidate.canonicalNameHint,
      aliases: [candidate.canonicalNameHint],
      objectType: candidate.objectType,
      ownerCharacterIds: candidate.ownerHint ? [candidate.ownerHint] : [],
      currentHolderCharacterId: candidate.ownerHint,
      currentLocationDescription: candidate.locationHint,
      operationalStatus: candidate.operationalHint || 'UNKNOWN',
      mobilityStatus: candidate.mobilityHint || 'UNKNOWN',
      continuityImportance: candidate.continuityImportance,
      sourceChapterId: String(params.chapterNumber),
      extractionVersion: 1,
    });
    if (candidate.requiresOpenObligation) {
      physical_continuity_obligations.push({
        obligationId: `obligation_${params.chapterNumber}_${slugify(objectId)}`,
        objectId,
        originatingChapter: params.chapterNumber,
        status: 'OPEN',
        obligation: candidate.objectType === 'VEHICLE' ? 'EXPLAIN_ABANDONMENT' : 'EXPLAIN_LOCATION',
        description: `${candidate.ownerHint || 'Owner'}'s ${candidate.canonicalNameHint} remains unresolved at ${candidate.locationHint || 'the last known location'}.`,
        requiredByChapter: params.chapterNumber + 1,
      });
    }
  }

  for (const issue of params.validation.incompleteObjectStates) {
    const index = continuity_objects.findIndex((entry) => {
      if (!entry || typeof entry !== 'object') return false;
      const record = entry as Record<string, unknown>;
      return record.objectId === issue.objectId;
    });
    if (index < 0) continue;
    const record = { ...(continuity_objects[index] as Record<string, unknown>) };
    if (issue.field === 'mobilityStatus') record.mobilityStatus = 'IMMOBILE';
    if (issue.field === 'operationalStatus') record.operationalStatus = 'DISABLED';
    if (issue.field === 'continuityImportance') record.continuityImportance = 'REQUIRED_CARRYOVER';
    if (issue.field === 'currentLocationDescription' && !record.currentLocationDescription) {
      record.currentLocationDescription = 'outside homestead';
    }
    continuity_objects[index] = record;
  }

  return {
    continuity_objects,
    physical_continuity_obligations,
  };
}

export function applyDeterministicPhysicalCorrection(params: {
  chapterNumber: number;
  validation: PhysicalContinuityExtractionValidation;
  rawData: Record<string, unknown>;
}): Record<string, unknown> {
  const patch = buildPhysicalCorrectionPayload({
    chapterNumber: params.chapterNumber,
    validation: params.validation,
    currentPhysicalOutput: params.rawData,
  });
  return {
    ...params.rawData,
    continuity_objects: patch.continuity_objects,
    physical_continuity_obligations: patch.physical_continuity_obligations,
  };
}

export function logPhysicalContinuityExtraction(payload: Record<string, unknown>): void {
  console.log(`[PHYSICAL CONTINUITY EXTRACTION] ${JSON.stringify(payload)}`);
}

export function logPhysicalContinuityValidation(payload: Record<string, unknown>): void {
  console.log(`[PHYSICAL CONTINUITY VALIDATION] ${JSON.stringify(payload)}`);
}

export function logPhysicalContinuityCorrection(payload: Record<string, unknown>): void {
  console.log(`[PHYSICAL CONTINUITY CORRECTION] ${JSON.stringify(payload)}`);
}

export function logPhysicalContinuityMerge(payload: Record<string, unknown>): void {
  console.log(`[PHYSICAL CONTINUITY MERGE] ${JSON.stringify(payload)}`);
}

export function logPhysicalContinuityExtractionBlocked(details: PhysicalContinuityExtractionError['details']): void {
  console.error(`[PHYSICAL CONTINUITY EXTRACTION BLOCKED] ${JSON.stringify(details)}`);
}

export interface FinalizePhysicalContinuityResult {
  ledger: Ledger;
  validation: PhysicalContinuityExtractionValidation;
  correctionAttempted: boolean;
  correctionSucceeded: boolean;
}

export async function finalizePhysicalContinuityLedger(params: {
  chapterContent: string;
  chapterNumber: number;
  existingLedger: Ledger;
  rawData: Record<string, unknown>;
  projectId?: string;
  inspectorPhysicalStateChanges?: InspectorPhysicalStateChange[];
  applyCorrection?: (args: {
    validation: PhysicalContinuityExtractionValidation;
    rawData: Record<string, unknown>;
    logicalCallId: string;
  }) => Record<string, unknown> | Promise<Record<string, unknown>>;
}): Promise<FinalizePhysicalContinuityResult> {
  resetPhysicalCorrectionBudget();
  const deterministic = detectPhysicalObjectCandidates(params.chapterContent, params.chapterNumber);
  let rawData = normalizeArchivistPhysicalResponse({ ...params.rawData });
  const inspector = filterInspectorPhysicalStateChangesForFinalProse(
    params.inspectorPhysicalStateChanges || [],
    params.chapterContent,
    params.chapterNumber,
  );
  const candidates = unionPhysicalCandidates({
    prose: params.chapterContent,
    chapterNumber: params.chapterNumber,
    inspectorChanges: inspector,
  });

  logPhysicalCandidateSources({
    deterministicCount: deterministic.length,
    inspectorCount: inspector.length,
    deduplicatedCount: candidates.length,
    highConfidenceCount: candidates.filter((candidate) => candidate.confidence === 'HIGH').length,
    mediumConfidenceCount: candidates.filter((candidate) => candidate.confidence === 'MEDIUM').length,
    candidateIds: candidates.map((candidate) => candidate.candidateId),
  });

  logPhysicalArchivistResponseShape(rawData, params.chapterNumber, {
    projectId: params.projectId,
    existingObjects: params.existingLedger.continuity_objects,
  });

  let validation = validatePhysicalContinuityExtraction({
    candidates,
    rawData,
    chapterNumber: params.chapterNumber,
    existingLedger: params.existingLedger,
    projectId: params.projectId,
    manifest: buildArchivistPhysicalCandidateManifest(candidates, inspector),
  });

  const initialMergedPreview = mergePhysicalContinuityLedger(
    params.existingLedger,
    rawData,
    params.chapterNumber,
    { projectId: params.projectId },
  );
  logPhysicalCandidateDiagnostics(
    buildPhysicalCandidateDiagnostics({
      candidates,
      validation,
      mergedPreview: initialMergedPreview,
    }),
  );

  logPhysicalContinuityExtraction({
    chapterNumber: params.chapterNumber,
    candidateIds: candidates.map((candidate) => candidate.candidateId),
    extractedObjectIds: validation.normalizedObjects.map((object) => object.objectId),
    rejectedObjectIds: [],
    missingFieldNames: validation.incompleteObjectStates.map((issue) => issue.field),
    obligationsOpened: validation.missingRequiredObligations.length,
    obligationsResolved: 0,
    mergedObjectCount: validation.normalizedObjects.length,
  });

  logPhysicalContinuityValidation({
    chapterNumber: params.chapterNumber,
    candidateIds: candidates.map((candidate) => candidate.candidateId),
    extractedObjectIds: validation.normalizedObjects.map((object) => object.objectId),
    rejectedObjectIds: [],
    missingFieldNames: validation.incompleteObjectStates.map((issue) => issue.field),
    obligationsOpened: validation.missingRequiredObligations.length,
    obligationsResolved: 0,
    mergedObjectCount: validation.normalizedObjects.length,
    missingRequiredCandidateIds: validation.missingRequiredObjects.map((candidate) => candidate.candidateId),
  });

  let correctionAttempted = false;
  let correctionSucceeded = false;

  if (!validation.valid && params.applyCorrection && canAttemptPhysicalCorrection()) {
    correctionAttempted = true;
    recordPhysicalCorrectionUsed();
    const logicalCallId = `physical_correction_ch${params.chapterNumber}_${getPhysicalCorrectionBudget().logicalCorrectionsUsed}`;
    rawData = normalizeArchivistPhysicalResponse(
      await params.applyCorrection({ validation, rawData, logicalCallId }),
    );
    validation = validatePhysicalContinuityExtraction({
      candidates,
      rawData,
      chapterNumber: params.chapterNumber,
      existingLedger: params.existingLedger,
      projectId: params.projectId,
    });
    correctionSucceeded = validation.valid;
    const correctedMergedPreview = mergePhysicalContinuityLedger(
      params.existingLedger,
      rawData,
      params.chapterNumber,
      { projectId: params.projectId },
    );
    logPhysicalCandidateDiagnostics(
      buildPhysicalCandidateDiagnostics({
        candidates,
        validation,
        mergedPreview: correctedMergedPreview,
      }),
    );
    logPhysicalContinuityCorrection({
      chapterNumber: params.chapterNumber,
      logicalCallId,
      candidateIds: candidates.map((candidate) => candidate.candidateId),
      extractedObjectIds: validation.normalizedObjects.map((object) => object.objectId),
      rejectedObjectIds: [],
      missingFieldNames: validation.incompleteObjectStates.map((issue) => issue.field),
      obligationsOpened: validation.missingRequiredObligations.length,
      obligationsResolved: correctionSucceeded ? 1 : 0,
      mergedObjectCount: validation.normalizedObjects.length,
      missingRequiredCandidateIds: validation.missingRequiredObjects.map((candidate) => candidate.candidateId),
    });
  }

  if (!validation.valid) {
    throw new PhysicalContinuityExtractionError(
      'Physical continuity extraction incomplete.',
      {
        chapterNumber: params.chapterNumber,
        candidateCount: candidates.length,
        missingObjectCount: validation.missingRequiredObjects.length,
        incompleteStateCount: validation.incompleteObjectStates.length,
        missingObligationCount: validation.missingRequiredObligations.length,
        correctionAttempted,
        correctionSucceeded,
      },
      { ...params.rawData, ...rawData },
    );
  }

  const physicalContinuity = mergePhysicalContinuityLedger(
    params.existingLedger,
    rawData,
    params.chapterNumber,
    { projectId: params.projectId },
  );
  const obligationsResolved = (physicalContinuity.physical_continuity_obligations || []).filter(
    (obligation) => obligation.status === 'RESOLVED',
  ).length;

  logPhysicalContinuityMerge({
    chapterNumber: params.chapterNumber,
    candidateIds: candidates.map((candidate) => candidate.candidateId),
    extractedObjectIds: (physicalContinuity.continuity_objects || []).map((object) => object.objectId),
    rejectedObjectIds: [],
    missingFieldNames: [],
    obligationsOpened: (physicalContinuity.physical_continuity_obligations || []).filter((o) => o.status === 'OPEN').length,
    obligationsResolved,
    mergedObjectCount: (physicalContinuity.continuity_objects || []).length,
  });

  return {
    ledger: physicalContinuity,
    validation,
    correctionAttempted,
    correctionSucceeded,
  };
}
